import consumer from "./consumer"

// Matches $gl-mobile-breakpoint (768px) — same convention as sidebar_drill.js.
// The CSS media query is max-width, so 768 itself counts as mobile.
const MOBILE_BREAKPOINT = 768

// Ambient team-wide chatter (social proof, celebrations). On a phone the toast
// stack is full-width at the bottom of the screen, so sale rebroadcasts pile up
// over form Save buttons (#toast-container in _toast.scss). lead_assigned is
// deliberately NOT here — it's personally relevant to the receiving agent.
const AMBIENT_EVENT_TYPES = new Set(["lead_sold", "lead_purchased", "badge_earned", "user_welcome"])

consumer.subscriptions.create("RealtimeEventsChannel", {
  connected() {
    console.log("Connected to RealtimeEventsChannel")
    const isReconnect = this.hasConnectedOnce === true
    this.hasConnectedOnce = true

    // On reconnect (not first load), request missed events
    if (this.lastEventTimestamp) {
      this.perform("catch_up", { since: this.lastEventTimestamp })
    }

    // Direct user notifications are intentionally not stored in the channel's
    // global replay buffer. Let authoritative clients refetch after every real
    // reconnect so events missed during the gap cannot leave CRM state stale.
    if (isReconnect) {
      document.dispatchEvent(new CustomEvent("goat:realtime-reconnected"))
    }
  },

  disconnected() {
    console.log("Disconnected from RealtimeEventsChannel")
  },

  received(data) {
    // Always update timestamp so suppressed events don't create replay gaps
    this.lastEventTimestamp = Date.now() / 1000
    document.dispatchEvent(new CustomEvent("goat:realtime-event", { detail: data }))

    if (this.isSuppressed(data)) return
    if (data.message) {
      this.showRealtimeToast(data.message, data.type, data.duration_ms, data)
    }
  },

  isSuppressed(data) {
    // A toast the user explicitly initiated — one carrying a download link for
    // an artifact they asked for (e.g. an async bulk-print PDF export, #8925) —
    // is never ambient noise, so even the mobile chatter rule below must not
    // swallow it: it is the only handle on the finished PDF.
    if (data.download_url) return false
    // The callback-due reminder (Voice::Callbacks::ReminderNotifier, #12656)
    // is the agent's own scheduled nudge, not ambient chatter — and it is the
    // ONLY in-tab signal: the browser Notification deliberately declines for
    // a visible tab because this toast covers it, and the row is already
    // stamped reminder_sent_at, so nothing nudges again. Keyed on the payload
    // key the notifier sends (spec-pinned in reminder_notifier_spec.rb).
    if (data.callback_event) return false

    const type = data.type

    // On mobile viewports, ambient chatter is suppressed unconditionally: the
    // toast stack spans the bottom of the screen there, and sale rebroadcasts
    // (sale_rebroadcast_count × window AppSettings) stack up over Save buttons.
    // Action feedback (success/error/info) and lead_assigned still show.
    if (AMBIENT_EVENT_TYPES.has(type) && window.innerWidth <= MOBILE_BREAKPOINT) return true

    // The four pop-ups an agent or admin may turn off (#17598): one switch each,
    // per user or site-wide (RealtimeToastHelper). Every other toast — exports,
    // imports, new-lead alerts, ticket notices — always shows.
    const body = document.body.dataset
    if (type === "lead_sold" && body.hideSalesToasts === "true") return true
    if (type === "lead_purchased" && body.hidePurchaseToasts === "true") return true
    if (type === "user_welcome" && body.hideWelcomeToasts === "true") return true
    if (type === "badge_earned" && body.hideBadgeToasts === "true") return true
    return false
  },

  showRealtimeToast(message, eventType, durationMs, data = {}) {
    // Map event types to color classes
    const colorMap = {
      lead_sold: "bg-success",
      lead_purchased: "bg-warning",
      user_welcome: "bg-purple",
      badge_earned: "bg-purple",
      lead_assigned: "bg-info",
      success: "bg-success",
      error: "bg-danger",
      danger: "bg-danger",
      warning: "bg-warning",
      info: "bg-info"
    }
    const colorClass = colorMap[eventType] || "bg-info"
    // A download (an artifact the user asked for) or a page link (e.g. the
    // order owner's Review link on a Lead Sharing Request Access, #19638).
    // toast.js renders either only as a same-origin <a>.
    const link = data.download_url
      ? { href: data.download_url, label: data.download_label }
      : data.link_url
        ? { href: data.link_url, label: data.link_label }
        : null
    // A toast carrying a link is the only handle the user has on what it
    // points at, so it stays up until dismissed rather than auto-hiding after
    // 5s — long enough to miss while looking at another tab.
    const delay = link ? 0 : durationMs || 5000

    if (window.showToast) {
      window.showToast(message, colorClass, delay, link)
    } else {
      console.error("showToast function not found")
    }
  }
})
